/**
 * Is a source page the BRAND'S OWN page?
 *
 * WHY (ITC Tax brief run 4eda4aae, 2026-10-01): a results tile such as
 * "Data Centers. $5M. Property Tax Savings. Harris County, Texas." is a client
 * result only when the brand published it. The same tile on a news article, a
 * competitor's case study or a comptroller.texas.gov statistics page is somebody
 * else's number, and must never rank an angle first, open `proof_in_public`, or
 * count as the brand's proof.
 *
 * Pure. The rule is conservative: no website on file, an unparseable URL or any
 * different registrable domain all read as NOT brand-owned. Subdomains of the
 * brand's registrable domain (www., blog.) are the brand's own.
 */

const TWO_PART_SUFFIX_SECOND_LABELS = new Set(['co', 'com', 'org', 'net', 'gov', 'ac', 'edu']);

export interface BrandSiteIdentity {
  websiteUrl?: string | null;
  ownedDomains?: readonly string[] | null;
}

function hostOf(value: string | null | undefined): string | null {
  const raw = (value ?? '').trim();
  if (!raw) return null;
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    const host = url.hostname.toLowerCase().replace(/\.$/, '');
    return host.includes('.') ? host : null;
  } catch {
    return null;
  }
}

/** `blog.itctax.com` -> `itctax.com`; `www.acme.co.uk` -> `acme.co.uk`. */
export function registrableDomain(host: string): string {
  const labels = host.split('.');
  if (labels.length <= 2 || /^\d+(\.\d+){3}$/.test(host)) return host;
  const tld = labels[labels.length - 1];
  const second = labels[labels.length - 2];
  const take = tld.length === 2 && TWO_PART_SUFFIX_SECOND_LABELS.has(second) ? 3 : 2;
  return labels.slice(-take).join('.');
}

export function isBrandOwnedSource(
  sourceUrl: string | null | undefined,
  brand: BrandSiteIdentity | null | undefined,
): boolean {
  const sourceHost = hostOf(sourceUrl);
  if (!sourceHost || !brand) return false;
  const brandHosts = [brand.websiteUrl, ...(brand.ownedDomains ?? [])]
    .map(hostOf)
    .filter((host): host is string => host !== null);
  const source = registrableDomain(sourceHost);
  return brandHosts.some((host) => registrableDomain(host) === source);
}

/** The anchor-source finding's plain statement of the rule that was applied. */
export function resultOwnershipNote(input: {
  brandHost: string | null;
  foreignHosts: readonly string[];
}): string {
  if (!input.brandHost) {
    return 'No website is on file for this workspace, so no result on a page is read as the brand’s own.';
  }
  const foreign = [...new Set(input.foreignHosts)];
  return foreign.length > 0
    ? `Results on ${input.brandHost} read as the brand’s own; results on ${foreign.join(', ')} are not.`
    : `Results on ${input.brandHost} read as the brand’s own.`;
}

export function brandHostLabel(brand: BrandSiteIdentity | null | undefined): string | null {
  const host = hostOf(brand?.websiteUrl) ?? hostOf(brand?.ownedDomains?.[0]);
  return host ? registrableDomain(host) : null;
}

export function sourceHostLabel(url: string | null | undefined): string | null {
  const host = hostOf(url);
  return host ? registrableDomain(host) : null;
}
